import React, { useCallback, useEffect, useState } from "react";
import * as Scrivito from "scrivito";
import {
  Alert,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import NiceModal from "@ebay/nice-modal-react";
import { useConfiguratorPopupContext } from "providers/ConfiguratorPopUpProvider";
import { useMutation } from "urql";
import cx from "classnames";
import { sessionStorageDisabledConfirmationsKey } from "variables";
import { useTranslate } from "hooks";
import { Spinner } from "../Spinner";
import { Button } from "../Button";
import { Icon } from "../Icon/Icon";
import type { IconName } from "../Icon/iconNames";
import { Marker } from "../Marker";
import s from "./ConfigurationModal.module.scss";

type DisplayListItem = {
  id: string;
  title: string;
};

type Props = {
  iconName?: IconName;
  title?: React.ReactNode;
  alertText?: React.ReactNode;
  submitBtnText?: React.ReactNode;
  mutation?: Parameters<typeof useMutation>[0];
  params?: Record<string, unknown>;
  onConfirm?: () => Promise<unknown> | null | undefined | void;
  displayList?: DisplayListItem[];
  onCancel?: () => void;
  mutationContext?: Record<string, unknown>;
  withDisableCheckbox?: boolean;
  modalToDisable?: string;
  cancelBtnText?: React.ReactNode;
  isWithoutCancelBtn?: boolean;
  type?: "info" | "warning";
};

export const ConfirmationModal = NiceModal.create(
  Scrivito.connect(
    ({
      iconName = "trash-can-light",
      title,
      alertText,
      submitBtnText,
      mutation,
      params,
      onConfirm,
      displayList,
      onCancel,
      mutationContext,
      withDisableCheckbox,
      modalToDisable,
      cancelBtnText,
      isWithoutCancelBtn,
      type,
    }: Props) => {
      const translate = useTranslate();
      const modal = NiceModal.useModal();

      const [checked, setChecked] = useState(false);
      const [isFetching, setIsFetching] = useState(false);

      const [{ data, fetching }, execute] = useMutation(mutation!);
      const { disableExit, navFuncState, handleUpdate } =
        useConfiguratorPopupContext()!;

      useEffect(() => {
        if (data) {
          modal.resolve(data);
          modal.remove();
        }
      }, [data, modal]);

      const handleSubmit = useCallback(async () => {
        if (checked) {
          const prevItems =
            (JSON.parse(
              sessionStorage.getItem(sessionStorageDisabledConfirmationsKey) ??
                "null",
            ) as string[] | null) ?? [];

          sessionStorage.setItem(
            sessionStorageDisabledConfirmationsKey,
            JSON.stringify([...prevItems, modalToDisable]),
          );
        }

        if (onConfirm) {
          setIsFetching(true);
          const result = onConfirm();
          // TODO: This "if" is needed bc onConfirm prop might not be passed in useDeleteConfirmationCheck
          if (result) {
            result.finally(() => {
              setIsFetching(false);
              modal.resolve();
              modal.remove();
            });
          } else {
            modal.resolve();
            modal.remove();
          }
          return;
        }

        if (mutationContext) {
          execute(params, mutationContext);
          modal.resolve();
          modal.remove();
          return;
        }

        if (navFuncState) {
          handleUpdate();
          modal.resolve();
          modal.remove();
          return;
        }

        execute(params);
      }, [
        modalToDisable,
        checked,
        onConfirm,
        mutationContext,
        execute,
        params,
        modal,
        navFuncState,
        handleUpdate,
      ]);

      const handleCancel = useCallback(() => {
        if (!isFetching && onCancel) {
          onCancel();
          modal.resolve();
          modal.remove();
        } else {
          modal.hide();
        }
      }, [isFetching, onCancel, modal]);

      const handleCheckboxChanged = useCallback(() => {
        setChecked((val) => !val);
      }, []);

      return (
        <Modal
          isOpen={modal.visible}
          centered={true}
          size="md"
          toggle={isFetching ? undefined : modal.hide}
          className="confirmation-modal"
        >
          <ModalHeader
            toggle={modal.hide}
            tag="div"
            className={cx("pb-0", { "pe-none": isFetching })}
          />
          <ModalBody className="pt-2_5 d-flex row m-0">
            <div className="text-center pb-3">
              <Icon
                iconName={iconName}
                className="icon-48 text-gray-400"
                title={iconName}
              />
              <h5 className="mb-0 pt-2 text-gray-700">{title}</h5>
            </div>

            <Alert
              color={type === "info" ? "secondary" : "danger"}
              className={cx(
                "ml-4 mr-4 overflow-hidden p-0 fs-md d-flex flex-nowrap",
                {
                  "text-gray-700": type === "info",
                  "text-red": type !== "info",
                },
              )}
            >
              <Marker color={type === "info" ? "gray-400" : "red"} />
              <div className="d-inline-block align-top py-2_5 ps-2_5 w-100">
                <div className="fw-bold mb-1">
                  {type === "info" ? "Information" : translate("TEXT_WARNING")}
                </div>
                <div className="m-0 pe-2_5">{alertText}</div>
                {displayList && (
                  <ul className={s.scrollbar}>
                    {displayList.map((proj) => (
                      <li key={proj.id}>{proj.title}</li>
                    ))}
                  </ul>
                )}
              </div>
            </Alert>
          </ModalBody>
          <ModalFooter
            className={cx("pt-0", {
              "justify-content-between": withDisableCheckbox,
            })}
          >
            {withDisableCheckbox && (
              <FormGroup check className="mb-0">
                <Input
                  type="checkbox"
                  id="disable-confirmation"
                  checked={checked}
                  onChange={handleCheckboxChanged}
                />
                <Label check htmlFor="disable-confirmation">
                  {translate("DO_NOT_SHOW_WARNING")}
                </Label>
              </FormGroup>
            )}
            <div className="d-flex flex-nowrap m-0">
              {!isWithoutCancelBtn && (
                <Button
                  className={cx(
                    "btn-tertiary me-3",
                    (disableExit || isFetching) &&
                      "opacity-75 user-select-none pe-none",
                  )}
                  onClick={handleCancel}
                  data-testid="ConfirmationModal-cancel-btn"
                >
                  {cancelBtnText ?? translate("CANCEL")}
                </Button>
              )}
              <Button
                className={cx("d-flex align-items-center gap-2_5", {
                  "btn-primary": type === "info",
                  "btn-secondary": type !== "info",
                })}
                disabled={disableExit || fetching || isFetching}
                data-testid="ConfirmationModal-submit-btn"
                onClick={handleSubmit}
              >
                {submitBtnText}
                {(fetching || isFetching) && <Spinner size="xxs" />}
              </Button>
            </div>
          </ModalFooter>
        </Modal>
      );
    },
  ),
);

NiceModal.register("confirmation", ConfirmationModal);
